/**
 * Retina Slideshow - Art-Directed Responsive Images
 * Web Component for high-performance slideshow with:
 * - Fade, Slide, Slide-Fade, and Zoom transitions
 * - Auto-play with pause on hover
 * - Dot/Line navigation with progress indicators
 * - Touch/swipe support
 * - Keyboard accessibility
 * - Countdown timer support
 */

if (!customElements.get('retina-slideshow')) {
  /**
   * RetinaSlideshow Web Component
   * @class
   * @extends HTMLElement
   * @property {number} currentIndex - Currently active slide index (zero-based)
   * @property {boolean} isPlaying - Whether autoplay is currently running
   * @property {boolean} isPaused - Whether autoplay is temporarily paused
   * @property {number|null} autoplayTimer - setInterval ID for autoplay
   * @property {number} touchStartX - X coordinate where touch began
   * @property {number} touchEndX - X coordinate where touch ended
   * @property {number} swipeThreshold - Minimum pixels for swipe detection (default: 50)
   * @property {Array<number>} countdownIntervals - setInterval IDs for countdown timers
   */
  class RetinaSlideshow extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.isPlaying = false;
      this.isPaused = false;
      this.autoplayTimer = null;
      this.touchStartX = 0;
      this.touchEndX = 0;
      this.swipeThreshold = 50;
      this.countdownIntervals = [];
      // Store bound handler for proper cleanup
      this.boundVisibilityHandler = this.handleVisibilityChange.bind(this);
    }

    /**
     * Called when element is added to the DOM.
     * Initializes slideshow immediately or defers to lazy loading if available.
     */
    connectedCallback() {
      // Use lazy initialization if available, otherwise init immediately
      if (window.initLazyScript) {
        window.initLazyScript(this, this.init.bind(this));
      } else {
        this.init();
      }
    }

    /**
     * Called when element is removed from the DOM.
     * Cleans up timers and event listeners to prevent memory leaks.
     */
    disconnectedCallback() {
      this.stopAutoplay();
      this.removeEventListeners();
      this.clearCountdowns();
    }

    /**
     * Initializes the slideshow component.
     * Caches DOM elements, reads settings from data attributes, sets up event
     * listeners, initializes countdowns, and starts autoplay if enabled.
     */
    init() {
      // Cache DOM elements
      this.track = this.querySelector('.rs-track');
      this.slides = Array.from(this.querySelectorAll('.rs-slide'));
      this.navBtns = Array.from(this.querySelectorAll('.rs-nav-btn[data-slide-index]'));
      this.prevBtn = this.querySelector('button[name="prev"]');
      this.nextBtn = this.querySelector('button[name="next"]');
      this.pauseBtn = this.querySelector('.autoplay-btn');
      this.counterCurrent = this.querySelector('.rs-nav__counter-current');
      this.counterTotal = this.querySelector('.rs-nav__counter-total');

      if (this.slides.length <= 1) {
        // Still initialize countdown for single slide
        this.initCountdowns();
        return;
      }

      // Get settings from data attributes
      this.autoplay = this.dataset.autoplay === 'true';
      this.speed = (parseInt(this.dataset.speed, 10) || 5) * 1000;
      this.transition = this.dataset.transition || 'fade';
      this.pauseOnHover = this.dataset.pauseOnHover !== 'false';

      // Set up event listeners
      this.setupEventListeners();

      // Initialize countdown timers
      this.initCountdowns();

      // Start autoplay if enabled
      if (this.autoplay) {
        this.startAutoplay();
      }

      // Set initial ARIA states
      this.updateAriaStates();
    }

    /**
     * Sets up all event listeners for slideshow interaction.
     * Includes navigation buttons, arrows, pause/play, hover, touch, keyboard,
     * visibility change, and intersection observer for viewport detection.
     */
    setupEventListeners() {
      // Navigation buttons (dots/lines)
      this.navBtns.forEach((btn) => {
        btn.addEventListener('click', () => {
          const index = parseInt(btn.dataset.slideIndex, 10);
          this.goToSlide(index);
        });
      });

      // Arrow navigation
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this.prevSlide());
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this.nextSlide());
      }

      // Pause/Play button
      if (this.pauseBtn) {
        this.pauseBtn.addEventListener('click', () => this.toggleAutoplay());
      }

      // Pause on hover
      if (this.pauseOnHover && this.autoplay) {
        this.addEventListener('mouseenter', this.handleMouseEnter.bind(this));
        this.addEventListener('mouseleave', this.handleMouseLeave.bind(this));
        this.addEventListener('focusin', this.handleMouseEnter.bind(this));
        this.addEventListener('focusout', this.handleMouseLeave.bind(this));
      }

      // Touch/swipe support
      this.track.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: true });
      this.track.addEventListener('touchend', this.handleTouchEnd.bind(this), { passive: true });

      // Keyboard navigation
      this.addEventListener('keydown', this.handleKeyDown.bind(this));

      // Visibility change (pause when tab is hidden)
      document.addEventListener('visibilitychange', this.boundVisibilityHandler);

      // Intersection Observer (pause when not in viewport)
      this.setupIntersectionObserver();
    }

    /**
     * Removes global event listeners added during setup.
     * Uses stored bound handler reference for proper cleanup.
     */
    removeEventListeners() {
      document.removeEventListener('visibilitychange', this.boundVisibilityHandler);
    }

    /**
     * Sets up IntersectionObserver to pause autoplay when slideshow leaves viewport.
     * Resumes when 30% or more of slideshow becomes visible again.
     */
    setupIntersectionObserver() {
      if (!('IntersectionObserver' in window)) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              if (this.autoplay && this.isPaused) {
                this.resumeAutoplay();
              }
            } else {
              if (this.isPlaying) {
                this.pauseAutoplay();
              }
            }
          });
        },
        { threshold: 0.3 }
      );

      observer.observe(this);
    }

    // === Countdown Timer Methods ===

    /**
     * Initializes countdown timers for all slides that contain countdown elements.
     * Parses end date/time from data attributes and updates the DOM every second.
     * When a countdown ends, either hides the element or shows an end message.
     * Stores interval references in this.countdownIntervals for cleanup.
     */
    initCountdowns() {
      this.slides.forEach((slide, index) => {
        const countdown = slide.querySelector('.rs-countdown');
        if (!countdown) return;

        const endDate = countdown.dataset.endDate;
        const endTime = countdown.dataset.endTime || '00:00';
        const hideOnEnd = countdown.dataset.hideOnEnd === 'true';

        if (!endDate) return;

        // Parse the end date/time
        const endDateTime = new Date(`${endDate}T${endTime}:00`);

        // Update function
        const updateCountdown = () => {
          const now = new Date();
          const diff = endDateTime - now;

          if (diff <= 0) {
            // Timer ended
            const endTextEl = countdown.querySelector('.rs-countdown__end-text');
            const containerEl = countdown.querySelector('.rs-countdown__container');

            if (hideOnEnd) {
              countdown.classList.add('rs-countdown--hidden');
            } else if (endTextEl && containerEl) {
              containerEl.style.display = 'none';
              endTextEl.style.display = 'block';
            }

            // Clear this interval
            if (this.countdownIntervals[index]) {
              clearInterval(this.countdownIntervals[index]);
            }
            return;
          }

          // Calculate time units
          const days = Math.floor(diff / (1000 * 60 * 60 * 24));
          const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
          const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
          const seconds = Math.floor((diff % (1000 * 60)) / 1000);

          // Update DOM
          const daysEl = countdown.querySelector('[data-countdown="days"]');
          const hoursEl = countdown.querySelector('[data-countdown="hours"]');
          const minutesEl = countdown.querySelector('[data-countdown="minutes"]');
          const secondsEl = countdown.querySelector('[data-countdown="seconds"]');

          if (daysEl) daysEl.textContent = String(days).padStart(2, '0');
          if (hoursEl) hoursEl.textContent = String(hours).padStart(2, '0');
          if (minutesEl) minutesEl.textContent = String(minutes).padStart(2, '0');
          if (secondsEl) secondsEl.textContent = String(seconds).padStart(2, '0');
        };

        // Initial update
        updateCountdown();

        // Set interval
        this.countdownIntervals[index] = setInterval(updateCountdown, 1000);
      });
    }

    /**
     * Clears all countdown timer intervals.
     * Called during cleanup to prevent memory leaks.
     */
    clearCountdowns() {
      this.countdownIntervals.forEach((interval) => {
        if (interval) clearInterval(interval);
      });
      this.countdownIntervals = [];
    }

    // === Navigation Methods ===

    /**
     * Navigates to a specific slide by index with animated transition.
     * Handles CSS class management for exit/enter animations, updates navigation
     * button states, slide counter, and ARIA attributes for accessibility.
     * Resets autoplay timer after transition completes.
     * @param {number} index - Zero-based index of the target slide.
     * @param {string|null} [direction=null] - Transition direction ('next' or 'prev').
     *   If null, direction is inferred from current vs target index.
     */
    goToSlide(index, direction = null) {
      if (index === this.currentIndex || index < 0 || index >= this.slides.length) return;

      const prevSlide = this.slides[this.currentIndex];
      const nextSlide = this.slides[index];

      // Determine direction for slide transitions
      if (!direction) {
        direction = index > this.currentIndex ? 'next' : 'prev';
      }

      // Apply exit class to current slide
      prevSlide.classList.add('rs-slide--exit');
      if (direction === 'next') {
        prevSlide.classList.add('rs-slide--exit-left');
      }

      // Prepare entering slide
      if (this.transition === 'slide' && direction === 'prev') {
        nextSlide.style.transform = 'translateX(-100%)';
      }

      // Trigger transition
      requestAnimationFrame(() => {
        prevSlide.classList.remove('rs-slide--active');
        nextSlide.classList.add('rs-slide--active');

        // Update nav buttons
        const prevNavBtn = this.navBtns.find(btn => parseInt(btn.dataset.slideIndex, 10) === this.currentIndex);
        const nextNavBtn = this.navBtns.find(btn => parseInt(btn.dataset.slideIndex, 10) === index);

        if (prevNavBtn) prevNavBtn.classList.remove('rs-nav-btn--active');
        if (nextNavBtn) nextNavBtn.classList.add('rs-nav-btn--active');

        // Update counter
        if (this.counterCurrent) {
          this.counterCurrent.textContent = index + 1;
        }

        // Update ARIA states
        this.updateAriaStates();

        // Update current index
        this.currentIndex = index;

        // Clean up after transition
        setTimeout(() => {
          prevSlide.classList.remove('rs-slide--exit', 'rs-slide--exit-left');
          nextSlide.style.transform = '';

          // Reset autoplay timer
          if (this.isPlaying) {
            this.resetAutoplayTimer();
          }
        }, 800);
      });
    }

    /**
     * Advances to the next slide, wrapping to first slide if at end.
     */
    nextSlide() {
      const nextIndex = (this.currentIndex + 1) % this.slides.length;
      this.goToSlide(nextIndex, 'next');
    }

    /**
     * Goes to the previous slide, wrapping to last slide if at beginning.
     */
    prevSlide() {
      const prevIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
      this.goToSlide(prevIndex, 'prev');
    }

    // === Autoplay Methods ===

    /**
     * Starts the autoplay timer to advance slides automatically.
     * Uses the speed setting from data-speed attribute.
     */
    startAutoplay() {
      if (this.isPlaying) return;
      this.isPlaying = true;
      this.isPaused = false;
      this.dataset.paused = 'false';
      this.autoplayTimer = setInterval(() => this.nextSlide(), this.speed);
    }

    /**
     * Stops autoplay and clears the timer interval.
     */
    stopAutoplay() {
      this.isPlaying = false;
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    /**
     * Temporarily pauses autoplay (e.g., on hover or when out of viewport).
     * Sets isPaused flag so autoplay can be resumed later.
     */
    pauseAutoplay() {
      if (!this.isPlaying) return;
      this.isPaused = true;
      this.dataset.paused = 'true';
      this.stopAutoplay();
    }

    /**
     * Resumes autoplay after being paused.
     * Only works if autoplay was enabled and is currently paused.
     */
    resumeAutoplay() {
      if (!this.autoplay || !this.isPaused) return;
      this.isPaused = false;
      this.dataset.paused = 'false';
      this.startAutoplay();
      this.updatePauseButton();
    }

    /**
     * Toggles autoplay between playing and paused states.
     * Called when user clicks the pause/play button.
     */
    toggleAutoplay() {
      if (this.isPlaying) {
        this.pauseAutoplay();
      } else {
        this.isPaused = true; // Set this so resumeAutoplay works
        this.resumeAutoplay();
      }
      this.updatePauseButton();
    }

    /**
     * Updates the pause button's visual state and ARIA label.
     * Shows pause icon when playing, play icon when paused.
     */
    updatePauseButton() {
      if (!this.pauseBtn) return;
      if (this.isPlaying) {
        this.pauseBtn.setAttribute('aria-label', 'Pause slideshow');
        this.pauseBtn.classList.remove('is-paused');
      } else {
        this.pauseBtn.setAttribute('aria-label', 'Play slideshow');
        this.pauseBtn.classList.add('is-paused');
      }
    }

    /**
     * Resets the autoplay timer after manual navigation.
     * Also restarts the progress line animation on navigation buttons.
     */
    resetAutoplayTimer() {
      if (!this.autoplay || !this.isPlaying) return;
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = setInterval(() => this.nextSlide(), this.speed);

      // Reset line progress animation for active nav button
      const activeNavBtn = this.navBtns.find(btn => btn.classList.contains('rs-nav-btn--active'));
      if (activeNavBtn) {
        const line = activeNavBtn.querySelector('.rs-nav-btn__line');
        if (line) {
          line.style.animation = 'none';
          void line.offsetWidth;
          line.style.animation = '';
        }
      }
    }

    // === Event Handlers ===

    /**
     * Handles mouse entering the slideshow area.
     * Pauses autoplay to prevent distracting transitions while user is engaged.
     */
    handleMouseEnter() {
      if (this.autoplay && this.isPlaying) {
        this.pauseAutoplay();
      }
    }

    /**
     * Handles mouse leaving the slideshow area.
     * Resumes autoplay if it was paused due to hover.
     */
    handleMouseLeave() {
      if (this.autoplay && this.isPaused) {
        this.resumeAutoplay();
      }
    }

    /**
     * Records starting X position when touch begins.
     * @param {TouchEvent} event - The touchstart event
     */
    handleTouchStart(event) {
      this.touchStartX = event.changedTouches[0].screenX;
    }

    /**
     * Records ending X position and triggers swipe detection.
     * @param {TouchEvent} event - The touchend event
     */
    handleTouchEnd(event) {
      this.touchEndX = event.changedTouches[0].screenX;
      this.handleSwipe();
    }

    /**
     * Processes swipe gesture to navigate slides.
     * Swipe left goes to next slide, swipe right goes to previous.
     * Ignores swipes shorter than swipeThreshold (50px default).
     */
    handleSwipe() {
      const diff = this.touchStartX - this.touchEndX;

      if (Math.abs(diff) < this.swipeThreshold) return;

      if (diff > 0) {
        // Swiped left - go to next
        this.nextSlide();
      } else {
        // Swiped right - go to previous
        this.prevSlide();
      }
    }

    /**
     * Handles keyboard navigation for accessibility.
     * ArrowLeft/ArrowRight navigate slides, Space toggles autoplay.
     * @param {KeyboardEvent} event - The keydown event
     */
    handleKeyDown(event) {
      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          this.prevSlide();
          break;
        case 'ArrowRight':
          event.preventDefault();
          this.nextSlide();
          break;
        case ' ':
          event.preventDefault();
          if (this.isPlaying) {
            this.pauseAutoplay();
          } else {
            this.resumeAutoplay();
          }
          break;
      }
    }

    /**
     * Handles page visibility changes (tab switching).
     * Pauses autoplay when tab is hidden, resumes when visible again.
     */
    handleVisibilityChange() {
      if (document.hidden) {
        if (this.isPlaying) {
          this.pauseAutoplay();
        }
      } else {
        if (this.autoplay && this.isPaused) {
          this.resumeAutoplay();
        }
      }
    }

    // === Accessibility ===

    /**
     * Updates ARIA attributes for all slides and navigation buttons.
     * Sets aria-hidden on inactive slides, manages tabindex for focusable
     * elements within slides, and updates aria-current on nav buttons.
     * Called after each slide transition to maintain screen reader compatibility.
     */
    updateAriaStates() {
      this.slides.forEach((slide, index) => {
        const isActive = index === this.currentIndex;
        slide.setAttribute('aria-hidden', !isActive);

        // Update focusable elements
        const focusableElements = slide.querySelectorAll('a, button');
        focusableElements.forEach((el) => {
          el.setAttribute('tabindex', isActive ? '0' : '-1');
        });
      });

      this.navBtns.forEach((btn) => {
        const index = parseInt(btn.dataset.slideIndex, 10);
        btn.setAttribute('aria-current', index === this.currentIndex ? 'true' : 'false');
      });
    }
  }

  customElements.define('retina-slideshow', RetinaSlideshow);
}
